/**
 * Floating sky islands: a grassy slab on a rocky root, a few little trees on
 * top and, optionally, a stream pouring off the rim into the air. Islands bob
 * very slowly, each on its own phase.
 */
import type { IslandsLayer } from '@dropstack/core';
import { lerp, spreadPositions, type SceneSize } from '../layout.ts';
import { hash01, range, rngFor } from '../noise.ts';
import {
  bodyGradient,
  circleBlob,
  circlePath,
  ellipseBlob,
  ellipsePath,
  gloss,
  rgba,
  rimOf,
  shade,
  smoothLine,
  strokeRim,
  vGradient,
  type Blob,
  type Circle,
  type Ctx,
  type Point,
} from './draw.ts';
import type { PaintEnv, Painter } from './types.ts';

interface Isle {
  x: number;
  y: number;
  /** Width of the grassy top (px). */
  w: number;
  /** Depth of the rocky root below the top (px). */
  depth: number;
  /** Root outline, relative to (x, y): right edge → tip → left edge. */
  root: Point[];
  trees: { dx: number; h: number }[];
  /** −1 / +1: which rim the stream pours from; 0: no stream. */
  fall: -1 | 0 | 1;
  phase: number;
  seed: number;
}

function planIsles(layer: IslandsLayer, env: PaintEnv): Isle[] {
  const { size, seed } = env;
  const rng = rngFor(seed, 1);
  const xs = spreadPositions(layer.count, layer.spread ?? [0, 1], (i) => hash01(seed + 2, i));
  return xs.map((fx, i): Isle => {
    const w = layer.size * size.unit * range(rng, 0.75, 1.25);
    const depth = w * range(rng, 0.5, 0.75);
    const tipX = w * range(rng, -0.1, 0.1);
    const root: Point[] = [
      { x: w * 0.5, y: w * 0.02 },
      { x: w * 0.42, y: depth * 0.25 },
      { x: w * range(rng, 0.2, 0.3), y: depth * 0.55 },
      { x: tipX + w * 0.07, y: depth * 0.85 },
      { x: tipX, y: depth },
      { x: tipX - w * 0.08, y: depth * 0.8 },
      { x: -w * range(rng, 0.22, 0.32), y: depth * 0.5 },
      { x: -w * 0.44, y: depth * 0.22 },
      { x: -w * 0.5, y: w * 0.02 },
    ];
    const treeCount = layer.trees ? 1 + Math.floor(rng.next() * 3) : 0;
    const trees = Array.from({ length: treeCount }, (_, k) => ({
      dx: w * (treeCount === 1 ? range(rng, -0.2, 0.2) : lerp(-0.3, 0.3, k / (treeCount - 1)) + range(rng, -0.05, 0.05)),
      h: w * range(rng, 0.22, 0.34),
    }));
    return {
      x: fx * size.width,
      y: lerp(layer.yMin, layer.yMax, rng.next()) * size.height,
      w,
      depth,
      root,
      trees,
      fall: layer.waterfall ? (hash01(seed + 4, i) < 0.5 ? -1 : 1) : 0,
      phase: range(rng, 0, Math.PI * 2),
      seed: (seed + i * 131) >>> 0,
    };
  });
}

function rockRoot(ctx: Ctx, isle: Isle, x: number, y: number, rock: string, size: SceneSize): void {
  const pts = isle.root.map((p) => ({ x: x + p.x, y: y + p.y }));
  const path = () => {
    ctx.beginPath();
    ctx.moveTo(x - isle.w * 0.5, y);
    ctx.lineTo(x + isle.w * 0.5, y);
    smoothLine(ctx, pts, true);
    ctx.closePath();
  };
  path();
  ctx.fillStyle = vGradient(ctx, y, y + isle.depth, [shade(rock, 0.12), rock, shade(rock, -0.22)]);
  ctx.fill();
  ctx.save();
  ctx.clip();
  // Strata and a shaded right flank.
  ctx.strokeStyle = rgba(shade(rock, -0.2), 0.55);
  ctx.lineWidth = Math.max(1, isle.w * 0.012);
  ctx.beginPath();
  for (const f of [0.28, 0.5, 0.72]) {
    const yy = y + isle.depth * f;
    ctx.moveTo(x - isle.w * 0.5, yy);
    ctx.quadraticCurveTo(x, yy + isle.w * 0.03 * (f > 0.4 ? 1 : -1), x + isle.w * 0.5, yy);
  }
  ctx.stroke();
  ctx.beginPath();
  ellipsePath(ctx, x + isle.w * 0.32, y + isle.depth * 0.45, isle.w * 0.25, isle.depth * 0.7);
  ctx.fillStyle = rgba(shade(rock, -0.3), 0.25);
  ctx.fill();
  ctx.restore();
  path();
  strokeRim(ctx, rimOf(rock), size);
  // A few dangling roots.
  ctx.strokeStyle = shade(rock, -0.35);
  ctx.lineWidth = Math.max(1, isle.w * 0.008);
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (const k of [2, 5, 6]) {
    const p = pts[k];
    if (!p) continue;
    const len = isle.depth * (0.15 + 0.15 * hash01(isle.seed, k));
    ctx.moveTo(p.x, p.y);
    ctx.quadraticCurveTo(p.x + len * 0.2, p.y + len * 0.6, p.x - len * 0.1, p.y + len);
  }
  ctx.stroke();
}

function stream(ctx: Ctx, isle: Isle, x: number, y: number, water: string, size: SceneSize, time: number): void {
  const sx = x + isle.fall * isle.w * 0.36;
  const w = isle.w * 0.07;
  const len = isle.depth * 1.6;
  ctx.beginPath();
  ctx.moveTo(sx - w / 2, y);
  ctx.lineTo(sx + w / 2, y);
  ctx.quadraticCurveTo(sx + w * 0.7 + isle.fall * w * 0.3, y + len * 0.5, sx + w * 0.9 + isle.fall * w, y + len);
  ctx.lineTo(sx - w * 0.9 + isle.fall * w, y + len);
  ctx.quadraticCurveTo(sx - w * 0.7 + isle.fall * w * 0.3, y + len * 0.5, sx - w / 2, y);
  ctx.closePath();
  // Fades into mist as it falls.
  ctx.fillStyle = vGradient(ctx, y, y + len, [rgba(water, 0.95), [0.6, rgba(water, 0.55)], rgba(water, 0)]);
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.setLineDash([len * 0.1, len * 0.14]);
  ctx.lineDashOffset = -time * size.unit * 0.12;
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
  ctx.lineWidth = Math.max(1, w * 0.22);
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (const u of [-0.22, 0.2]) {
    ctx.moveTo(sx + u * w, y);
    ctx.quadraticCurveTo(sx + u * w + isle.fall * w * 0.3, y + len * 0.5, sx + u * w * 1.6 + isle.fall * w, y + len);
  }
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.lineDashOffset = 0;
  ctx.restore();
}

function grassTop(ctx: Ctx, isle: Isle, x: number, y: number, grass: string, size: SceneSize): void {
  const w = isle.w;
  const blobs: Blob[] = [{ x, y: y - w * 0.015, rx: w * 0.53, ry: w * 0.09 }];
  // Grass lip rolling over the rim.
  const lips = 7;
  for (let k = 0; k < lips; k++) {
    const u = k / (lips - 1) - 0.5;
    blobs.push({ x: x + u * w * 0.92, y: y + w * 0.035, rx: w * (0.06 + 0.02 * hash01(isle.seed + 5, k)), ry: w * 0.05 });
  }
  ellipseBlob(ctx, blobs, bodyGradient(ctx, grass, y - w * 0.1, y + w * 0.08, 0.2, -0.1), rimOf(grass), size);
  ctx.beginPath();
  ellipsePath(ctx, x - w * 0.12, y - w * 0.045, w * 0.3, w * 0.035);
  ctx.fillStyle = rgba(shade(grass, 0.3), 0.6);
  ctx.fill();
}

function tinyTree(ctx: Ctx, x: number, y: number, h: number, color: string, size: SceneSize): void {
  ctx.beginPath();
  ctx.moveTo(x - h * 0.05, y);
  ctx.lineTo(x - h * 0.03, y - h * 0.5);
  ctx.lineTo(x + h * 0.03, y - h * 0.5);
  ctx.lineTo(x + h * 0.05, y);
  ctx.closePath();
  ctx.fillStyle = '#9a6b47';
  ctx.fill();
  strokeRim(ctx, rimOf('#9a6b47'), size, 0.7);
  const crown: Circle[] = [
    { x, y: y - h * 0.68, r: h * 0.3 },
    { x: x - h * 0.2, y: y - h * 0.52, r: h * 0.2 },
    { x: x + h * 0.2, y: y - h * 0.54, r: h * 0.21 },
  ];
  circleBlob(ctx, crown, bodyGradient(ctx, color, y - h, y - h * 0.3), rimOf(color), size, 0.8);
  gloss(ctx, x - h * 0.1, y - h * 0.8, h * 0.1, h * 0.05, 0.45);
}

export const islandsPainter: Painter<IslandsLayer> = {
  extent: (layer, { size }) => ({
    top: layer.yMin * size.height - layer.size * size.unit * 0.55,
    bottom: layer.yMax * size.height + layer.size * size.unit * 2.2,
  }),
  moving(ctx, layer, env) {
    const { size, time } = env;
    for (const isle of planIsles(layer, env)) {
      const bob = Math.sin(time * 0.45 + isle.phase) * size.unit * 0.008;
      const x = isle.x;
      const y = isle.y + bob;
      rockRoot(ctx, isle, x, y, layer.rock, size);
      if (isle.fall !== 0 && layer.waterfall) stream(ctx, isle, x, y, layer.waterfall, size, time);
      grassTop(ctx, isle, x, y, layer.grass, size);
      if (layer.trees) for (const t of isle.trees) tinyTree(ctx, x + t.dx, y - isle.w * 0.03, t.h, layer.trees, size);
      // Little flowers dotted on the grass.
      ctx.beginPath();
      for (let k = 0; k < 4; k++) circlePath(ctx, x + (hash01(isle.seed, k + 20) - 0.5) * isle.w * 0.8, y - isle.w * 0.01, isle.w * 0.012);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
    }
  },
};
